import { DEFAULT_DEBOUNCE_DELAY, STATUSPAGE } from '@repo/ui';
import debounce from 'debounce';
import {
  GetAllProfessionalsUseCaseInterface,
  GetInternshipLocationUseCaseInterface,
  UnlinkProfessionalUseCaseInterface,
} from 'domain/usecases';
import { ORDER_TYPE } from 'domain/value-objects/order-type';
import { PERSONS_ORDER_COLUMN } from 'domain/value-objects/persons-order-column';
import { ProfessionalData } from 'domain/value-objects/professional-data';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadInternshipLocation } from 'presentation/config/store/internship-location/internship-location-slice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import {
  loadProfessionalsData,
  clearProfessionalsData,
} from 'presentation/config/store/professionals/professionals-lists-slice';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface IPaginationMetadata {
  selectedPage: number;
  rowsPerPage: number;
  orderColumn: PERSONS_ORDER_COLUMN;
  sortDirection: ORDER_TYPE;
  searchString: string;
}

export const useProfessionalsLists = () => {
  // Hooks
  const { professionalService, organizationService, courseService } =
    useService();
  const { internshipLocationService } = useService();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getLegends } = useLegends();

  // States
  const { coursesUser } = useAppSelector((state) => state.courses);
  const { organizationsUser } = useAppSelector((state) => state.organizations);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [unlinkModalButtonsStatusPage, setUnlinkModalButtonsStatusPage] =
    useState(STATUSPAGE.DONE);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<
    string | null
  >(null);
  const [paginationMetadata, setPaginationMetadata] =
    useState<IPaginationMetadata>({
      selectedPage: 0,
      rowsPerPage: 10,
      orderColumn: PERSONS_ORDER_COLUMN.NAME,
      sortDirection: ORDER_TYPE.ASC,
      searchString: '',
    });
  const [listStatusPage, setListStatusPage] = useState(STATUSPAGE.LOADING);
  const {
    professionalsData,
    //  professionalsFilters
  } = useAppSelector((state) => state.professionalsList);
  const { groupsUser } = useAppSelector((state) => state.groups);
  const { data: internshipLocationsSelectOptions } = useAppSelector(
    (state) => state.internshipLocation,
  );

  // Methods
  const toggleUnlinkModal = useCallback(() => {
    setIsUnlinkModalOpen((prevState) => !prevState);
  }, []);

  const changePaginationMetadata = <T extends keyof IPaginationMetadata>(
    key: T,
    value: IPaginationMetadata[T],
  ) => {
    setPaginationMetadata((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handlePageChange = useCallback((page: number) => {
    setListStatusPage(STATUSPAGE.LOADING);
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    setListStatusPage(STATUSPAGE.LOADING);
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const handleSortRemote = useCallback(
    (
      selectedColumn: TableColumn<ProfessionalData>,
      sortDirection: SortOrder,
    ) => {
      setListStatusPage(STATUSPAGE.LOADING);
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as PERSONS_ORDER_COLUMN,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handleSearchRemote = useCallback((searchString: string) => {
    setListStatusPage(STATUSPAGE.LOADING);
    changePaginationMetadata('searchString', searchString);
  }, []);

  const requestGetAllProfessionals = useCallback(
    async (params: GetAllProfessionalsUseCaseInterface.Params) => {
      return professionalService
        .getAllProfessionals(params)
        .then((res) => {
          dispatch(loadProfessionalsData(res));
        })
        .catch((err) => {
          dispatch(clearProfessionalsData());
          notifyError(err.message);
        })
        .finally(() => {
          setListStatusPage(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetInternshipLocationsSelectOptions = useCallback(
    async (params: GetInternshipLocationUseCaseInterface.Params) => {
      await internshipLocationService
        .getInternshipLocation(params)
        .then((res) => {
          dispatch(loadInternshipLocation(res));
        })
        .catch((err) => {
          notifyError(err);
        });
    },
    [],
  );

  const requestUnlinkProfessional = useCallback(
    async (
      params: UnlinkProfessionalUseCaseInterface.Params,
      profileType: PROFILE_TYPE,
    ) => {
      setUnlinkModalButtonsStatusPage(STATUSPAGE.LOADING);
      await professionalService
        .unlinkProfessional(params)
        .then((res) => {
          setUnlinkModalButtonsStatusPage(STATUSPAGE.DONE);
          notifySuccess(res.message);
          toggleUnlinkModal();
          requestGetAllProfessionals({
            page: paginationMetadata.selectedPage.toString(),
            length: paginationMetadata.rowsPerPage.toString(),
            order: paginationMetadata.sortDirection,
            orderColumn: paginationMetadata.orderColumn,
            query: paginationMetadata.searchString,
            profileType,
          });
        })
        .catch((err) => {
          setUnlinkModalButtonsStatusPage(STATUSPAGE.DONE);
          notifyError(err.message);
        });
      return;
    },
    [],
  );

  const debouncedRequestGetAllProfessionals = useMemo(
    () =>
      debounce((valuesToSubmit: GetAllProfessionalsUseCaseInterface.Params) => {
        requestGetAllProfessionals(valuesToSubmit);
      }, DEFAULT_DEBOUNCE_DELAY),

    [],
  );

  const requestGetAllOrganizationsUser = useCallback(async () => {
    await organizationService
      .getAllOrganizationsUser()
      .then((res) => dispatch(loadOrganizationsUser(res)))
      .catch((err) => notifyError(err));
  }, []);

  const requestGetAllCoursesUser = useCallback(async (entityId?: string) => {
    await courseService
      .getAllCoursesUser({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => notifyError(err));
  }, []);

  const removeProfessionalsData = useCallback(() => {
    dispatch(clearProfessionalsData());
  }, []);

  // const saveProfessionalsFilters = useCallback(
  //   (params: IProfessionalsListsFilters) => {
  //     dispatch(loadProfessionalsFilters(params));
  //   },
  //   [],
  // );

  return {
    // States
    coursesUser,
    organizationsUser,
    professionalsData,
    selectedProfessionalId,
    unlinkModalButtonsStatusPage,
    // professionalsFilters,
    paginationMetadata,
    debouncedRequestGetAllProfessionals,
    listStatusPage,
    groupsUser,
    internshipLocationsSelectOptions,
    isUnlinkModalOpen,
    // Methods
    // saveProfessionalsFilters,
    navigate,
    getLegends,
    toggleUnlinkModal,
    setSelectedProfessionalId,
    setListStatusPage,
    requestGetAllProfessionals,
    requestGetInternshipLocationsSelectOptions,
    requestGetAllOrganizationsUser,
    requestGetAllCoursesUser,
    requestUnlinkProfessional,
    removeProfessionalsData,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortRemote,
    handleSearchRemote,
  };
};
